import {inject, injectable} from "inversify";
import {Player} from "@/modules/team/entity";
import {TransferListing, TransferOffer} from "@/modules/transfer-market/entity";
import {BeforeTransferListingCreatedEvent} from "@/modules/transfer-market/events/BeforeTransferListingCreatedEvent";
import {AfterTransferListingCreatedEvent} from "@/modules/transfer-market/events/AfterTransferListingCreatedEvent";
import MarketValueCalculator from "@/modules/team/services/MarketValueCalculator";
import {EventEmitter} from "@/modules/base/events";
import {NegotiationCloser} from "@/modules/diplomacy/services/NegotiationCloser";
import {BeforeTransferOfferAbortedEvent} from "@/modules/transfer-market/events/BeforeTransferOfferAbortedEvent";
import {AfterTransferOfferAbortedEvent} from "@/modules/transfer-market/events/AfterTransferOfferAbortedEvent";

@injectable()
export default class ListingHandler {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(MarketValueCalculator) private marketValueCalculator: MarketValueCalculator,
    @inject(NegotiationCloser) private negotiationCloser: NegotiationCloser,
  ) {
  }

  public async remove(listing: TransferListing) {
    await listing.offers.forEachAsync(async (offer: TransferOffer) => {
      await this.em.emitAsync(new BeforeTransferOfferAbortedEvent(offer, listing.player));

      await this.negotiationCloser.abort(offer.playerNegotiation);
      if (offer.teamNegotiation) {
        await this.negotiationCloser.abort(offer.teamNegotiation);
      }

      await this.em.emitAsync(new AfterTransferOfferAbortedEvent(offer, listing.player));
      await offer.remove();
    });

    await listing.remove();
  }

  public async create(player: Player, price?: number) {
    if (!price) {
      price = await this.marketValueCalculator.calculate(player);
    }

    await this.em.emitAsync(new BeforeTransferListingCreatedEvent(player, price));

    const listing = new TransferListing();
    listing.player = player;
    listing.price = price;
    await listing.save();

    await this.em.emitAsync(new AfterTransferListingCreatedEvent(listing));

    return listing;
  }
}
